import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, RefreshCw, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";

// Private Growth Agent console — nightly AI analysis reports.
export default function AdminGrowthPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [running, setRunning] = useState(false);

  const load = () =>
    api.get("/admin/growth/reports")
      .then((r) => setReports(r.data.items || []))
      .catch(() => toast.error("Admin access required."));

  useEffect(() => { if (user) load(); }, [user]);

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/admin/growth/run");
      toast.success("Analysis complete.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white px-4 sm:px-8 py-8" data-testid="admin-growth-page">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate("/admin")} className="text-gray-400 text-sm flex items-center gap-1 mb-4" data-testid="growth-back-btn">
          <ArrowLeft className="h-4 w-4" /> Admin console
        </button>
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Brain className="h-6 w-6 text-emerald-400" />
            <h1 className="text-2xl font-bold">Growth Agent</h1>
          </div>
          <Button onClick={runNow} disabled={running} className="bg-emerald-500 hover:bg-emerald-400 text-gray-950 font-semibold" data-testid="growth-run-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${running ? "animate-spin" : ""}`} />
            {running ? "Analyzing…" : "Run analysis now"}
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Runs automatically every night. Each report ranks friction points, lost
          opportunities, weekly experiments, and anomalies from live metrics.
        </p>
        {reports.length === 0 && (
          <div className="text-gray-500 text-sm border border-dashed border-gray-800 rounded-xl p-8 text-center" data-testid="growth-empty">
            No reports yet — hit "Run analysis now".
          </div>
        )}
        <div className="space-y-6">
          {reports.map((r) => (
            <div key={r.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="growth-report-card">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <span>{new Date(r.created_at).toLocaleString()}</span>
                <span className="uppercase tracking-widest">{r.trigger}</span>
              </div>
              {r.report_md ? (
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-200 leading-relaxed">{r.report_md}</pre>
              ) : (
                <p className="text-rose-400 text-sm">Analysis failed: {r.error}</p>
              )}
              <details className="mt-3 text-xs text-gray-500">
                <summary className="cursor-pointer">Raw metrics</summary>
                <pre className="mt-2 overflow-x-auto">{JSON.stringify(r.metrics, null, 1)}</pre>
              </details>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
